import React from 'react';

/**
 * DashboardCard
 * ------------------------------------------------------------------
 * The single reusable shell behind every widget on the dashboard:
 * KPI cards, the primary-focus card, active session, hours logged,
 * recent activity — all of them are just this shell with different
 * content dropped in via `children`, plus an optional `footer` slot
 * pinned to the bottom of the card.
 *
 * @param {string|React.ReactNode} title - card heading, top-left
 * @param {React.ReactNode} subtitle - optional text under the title (combined header)
 * @param {React.ReactNode} topRight - e.g. a value, badge, or "View All" link
 * @param {'neutral'|'pink'|'green'|'purple'|'blue'|'peach'|'cream'|'red'|'darkgreen'|'brown'|'orange'|'lightblue'|'pinkbrown'|'lightteal'} tone - background tint
 * @param {React.ReactNode} footer - optional action pinned to the bottom (e.g. "View Tasks →")
 * @param {boolean} fullHeight - stretch to fill its grid row so a row of
 *   cards with uneven content still shares one bottom edge (default true)
 */
const DashboardCard = ({
  title,
  subtitle,
  topRight,
  tone = 'neutral',
  footer,
  children,
  className = '',
  fullHeight = true,
  as: Tag = 'div',
}) => {
  const classes = [
    'dcard',
    `dcard--${tone}`,
    fullHeight ? 'dcard--full-height' : '',
    subtitle ? 'dcard--with-subtitle' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Tag className={classes}>
      {(title || topRight || subtitle) && (
        <div className="dcard__title-column">
          <div className="dcard__title-block">
            {title && <h3 className="dcard__title">{title}</h3>}
            {subtitle && <div className="dcard__subtitle">{subtitle}</div>}
          </div>
          {topRight && <div className="dcard__top-right">{topRight}</div>}
        </div>
      )}
      <div className="dcard__content-column">
        {children && <div className="dcard__body">{children}</div>}
        {footer && <div className="dcard__footer">{footer}</div>}
      </div>
    </Tag>
  );
};

export default DashboardCard;
